/**
 * LMS Operations — Tab 1 "Ext" popup.
 *
 * Sits immediately to the right of "View Summary" on the loan profile header and
 * opens the borrower-identity / business-premises record (Loan ID … Borrower
 * Occupation Category) with JSON + CSV downloads of exactly that record.
 *
 * WHY A SECOND DIALOG RATHER THAN MORE ROWS IN LoanSummaryDialog: the summary
 * answers money/mandate questions; this one answers "who and where is this
 * borrower", which is what lender queries and compliance checks ask for. Merging
 * them would produce one ~30-row popup that serves neither job well.
 *
 * NO NETWORK CALL OF ITS OWN — the data arrives as `loan_ext` on the SAME
 * `LoanProfileView` response the page already fetched, exactly like
 * `loan_summary`, so opening the popup is instant.
 *
 * WHY THE FIELD LIST IS IMPORTED RATHER THAN WRITTEN HERE: `LOAN_EXT_FIELDS`
 * (lmsStaffApi.ts, mirroring `LOAN_EXT_FIELDS` in views_staff_ops.py) is the
 * single order+label definition driving the on-screen rows, the JSON key order
 * and the CSV header. Duplicating it per consumer is how an export silently
 * stops matching the screen.
 */
import React from "react";
import { Download, FileJson, FileSpreadsheet, IdCard } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { LOAN_EXT_FIELDS, LoanExt } from "./lmsStaffApi";
import { TWO_LINE_BUTTON_CLASS, TwoLineLabel } from "./twoLineButton";

/** Triggers a browser download of `blob` under `filename`. */
const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Without this the object URL (and the blob behind it) leaks for the
    // lifetime of the document.
    URL.revokeObjectURL(url);
};

/**
 * Characters that make Excel / Google Sheets treat a cell as a FORMULA.
 * Mirrors `_CSV_INJECTION_PREFIXES` / `_csv_safe()` in views_staff_ops.py —
 * every value here (applicant name, entity name, address) is user-supplied, so a
 * value like `=HYPERLINK(...)` opened by a staff member is a real exfiltration
 * vector, not a formatting nicety.
 */
const CSV_INJECTION_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

const csvCell = (value: unknown): string => {
    if (value === null || value === undefined) return '""';
    let text = String(value);
    if (CSV_INJECTION_PREFIXES.some((prefix) => text.startsWith(prefix))) {
        text = `'${text}`;
    }
    // Quote unconditionally and double any embedded quote — the simplest form
    // that is correct for values containing commas, quotes or newlines.
    return `"${text.replace(/"/g, '""')}"`;
};

const displayValue = (value: LoanExt[keyof LoanExt]) => {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
};

export interface LoanExtDialogProps {
    ext: LoanExt;
    /** Used only to name the downloaded files. */
    loanId: string;
}

const LoanExtDialog: React.FC<LoanExtDialogProps> = ({ ext, loanId }) => {
    /**
     * A plain object in LOAN_EXT_FIELDS order, keyed by the backend field names
     * — the payload both downloads are built from.
     */
    const orderedRecord = React.useMemo(() => {
        const record: Record<string, unknown> = {};
        LOAN_EXT_FIELDS.forEach(({ key }) => {
            record[key] = ext[key] ?? null;
        });
        return record;
    }, [ext]);

    const handleDownloadJson = () => {
        downloadBlob(
            new Blob([JSON.stringify(orderedRecord, null, 2)], {
                type: "application/json",
            }),
            `loan_ext_${loanId}.json`
        );
    };

    const handleDownloadCsv = () => {
        const header = LOAN_EXT_FIELDS.map(({ label }) => csvCell(label)).join(",");
        const row = LOAN_EXT_FIELDS.map(({ key }) => csvCell(ext[key])).join(",");
        // The UTF-8 BOM leads the file so Excel renders Indian names correctly
        // instead of mojibake — same reason the server-side register export
        // prepends one.
        downloadBlob(
            new Blob([`\uFEFF${header}\n${row}\n`], { type: "text/csv;charset=utf-8;" }),
            `loan_ext_${loanId}.csv`
        );
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-label="View extended borrower details"
                    title="View extended borrower details"
                    className={TWO_LINE_BUTTON_CLASS}
                >
                    <IdCard className="h-4 w-4" />
                    <TwoLineLabel top="View" bottom="Ext" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {ext.applicant_name || "Customer"} — Extended Details
                    </DialogTitle>
                    <DialogDescription>
                        {loanId} · Read-only. Downloads carry the raw stored values.
                    </DialogDescription>
                </DialogHeader>

                {/* Downloads at the top, matching the Summary popup */}
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={handleDownloadJson}
                    >
                        <FileJson className="h-4 w-4" /> Download JSON
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={handleDownloadCsv}
                    >
                        <FileSpreadsheet className="h-4 w-4" /> Download CSV
                    </Button>
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400">
                        <Download className="h-3.5 w-3.5" /> {LOAN_EXT_FIELDS.length}{" "}
                        fields
                    </span>
                </div>

                <Separator />

                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                        <tbody>
                            {LOAN_EXT_FIELDS.map(({ key, label }, index) => (
                                <tr
                                    key={key}
                                    className={
                                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                    }
                                >
                                    <th className="w-1/2 px-3 py-2 text-left align-top text-xs font-medium uppercase tracking-wide text-gray-500">
                                        {label}
                                    </th>
                                    <td className="px-3 py-2 align-top text-gray-900 break-words">
                                        {displayValue(ext[key])}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LoanExtDialog;
